/**
 * IntakeForm - Public multi-step patient registration form
 * Accessible without authentication
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useViewOnly } from '../contexts';
import './IntakeForm.css';

const API_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

const IntakeForm = ({ language: initialLang = 'he' }) => {
  const [language, setLanguage] = useState(initialLang);
  const [currentStep, setCurrentStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const signatureRef = useRef(null);
  const isRTL = language === 'he';
  // IntakeForm is a public route mounted outside ViewOnlyProvider — useViewOnly
  // falls back to { isViewOnly: false }. Gating is wired per spec but never
  // blocks public submitters.
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Personal info
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    id_number: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    preferred_language: 'he',

    // Step 2: Emergency contact
    emergency_contact: {
      name: '',
      relationship: '',
      phone: ''
    },

    // Step 3: Medical history
    medical_history: {
      allergies: [],
      medications: [],
      chronic_conditions: [],
      previous_surgeries: [],
      blood_type: '',
      smoker: false,
      pregnant: false,
      last_dental_visit: '',
      dental_concerns: [],
      gum_disease_history: false,
      teeth_grinding: false,
      dental_anxiety: false
    },

    // Step 4: Insurance
    insurance_info: {
      provider: '',
      policy_number: '',
      subscriber_name: '',
      subscriber_id: ''
    },

    // Step 5: Consent
    consent_treatment: false,
    consent_privacy: false,
    consent_sms: false,
    consent_marketing: false,
    signature: '',
    how_did_you_hear: ''
  });

  // Custom input for allergies/medications
  const [customAllergy, setCustomAllergy] = useState('');
  const [customMedication, setCustomMedication] = useState('');

  // Translations
  const t = {
    he: {
      title: 'טופס קליטת מטופל חדש',
      subtitle: 'אנא מלא את הפרטים הבאים',
      step1: 'פרטים אישיים',
      step2: 'איש קשר לחירום',
      step3: 'היסטוריה רפואית',
      step4: 'ביטוח',
      step5: 'הסכמות וחתימה',
      next: 'הבא',
      back: 'חזור',
      submit: 'שלח טופס',
      required: 'שדה חובה',
      firstName: 'שם פרטי',
      lastName: 'שם משפחה',
      dateOfBirth: 'תאריך לידה',
      gender: 'מין',
      idNumber: 'תעודת זהות',
      phone: 'טלפון',
      email: 'אימייל',
      address: 'כתובת',
      city: 'עיר',
      preferredLang: 'שפה מועדפת',
      hebrew: 'עברית',
      english: 'English',
      contactName: 'שם איש קשר',
      relationship: 'קרבה',
      contactPhone: 'טלפון',
      allergies: 'אלרגיות',
      medications: 'תרופות קבועות',
      conditions: 'מחלות רקע',
      surgeries: 'ניתוחים קודמים',
      bloodType: 'סוג דם',
      smoker: 'מעשן/ת?',
      pregnant: 'בהריון?',
      lastDentalVisit: 'ביקור אחרון אצל רופא שיניים',
      dentalConcerns: 'דאגות או בעיות בשיניים',
      gumDisease: 'היסטוריה של מחלות חניכיים',
      teethGrinding: 'חריקת שיניים',
      dentalAnxiety: 'חרדה מרופא שיניים',
      insuranceProvider: 'חברת ביטוח',
      policyNumber: 'מספר פוליסה',
      subscriberName: 'שם המבוטח',
      subscriberId: 'מספר מבוטח',
      consentTreatment: 'אני מאשר/ת קבלת טיפולים רפואיים',
      consentPrivacy: 'קראתי ומאשר/ת את מדיניות הפרטיות',
      consentSMS: 'אני מסכים/ה לקבל הודעות SMS',
      consentMarketing: 'אני מסכים/ה לקבל חומרי שיווק',
      signature: 'חתימה',
      clearSignature: 'נקה חתימה',
      howDidYouHear: 'איך שמעת עלינו?',
      submitting: 'שולח...',
      successTitle: 'הטופס נשלח בהצלחה!',
      successMessage: 'תודה על מילוי הטופס. נציג מהמרפאה יצור איתך קשר בקרוב.',
      errorRequired: 'אנא מלא את כל השדות הנדרשים',
      addCustom: 'הוסף',
      yes: 'כן',
      no: 'לא',
      select: 'בחר...',
      none: 'אין',
      lessThan6Months: 'פחות מ-6 חודשים',
      sixTo12Months: '6-12 חודשים',
      oneToTwoYears: '1-2 שנים',
      moreThan2Years: 'יותר מ-2 שנים',
      never: 'מעולם לא'
    },
    en: {
      title: 'New Patient Intake Form',
      subtitle: 'Please fill in the following details',
      step1: 'Personal Information',
      step2: 'Emergency Contact',
      step3: 'Medical History',
      step4: 'Insurance',
      step5: 'Consent & Signature',
      next: 'Next',
      back: 'Back',
      submit: 'Submit Form',
      required: 'Required field',
      firstName: 'First Name',
      lastName: 'Last Name',
      dateOfBirth: 'Date of Birth',
      gender: 'Gender',
      idNumber: 'ID Number',
      phone: 'Phone',
      email: 'Email',
      address: 'Address',
      city: 'City',
      preferredLang: 'Preferred Language',
      hebrew: 'עברית',
      english: 'English',
      contactName: 'Contact Name',
      relationship: 'Relationship',
      contactPhone: 'Phone',
      allergies: 'Allergies',
      medications: 'Current Medications',
      conditions: 'Medical Conditions',
      surgeries: 'Previous Surgeries',
      bloodType: 'Blood Type',
      smoker: 'Smoker?',
      pregnant: 'Pregnant?',
      lastDentalVisit: 'Last Dental Visit',
      dentalConcerns: 'Dental Concerns',
      gumDisease: 'History of Gum Disease',
      teethGrinding: 'Teeth Grinding',
      dentalAnxiety: 'Dental Anxiety',
      insuranceProvider: 'Insurance Provider',
      policyNumber: 'Policy Number',
      subscriberName: 'Subscriber Name',
      subscriberId: 'Subscriber ID',
      consentTreatment: 'I consent to receiving medical treatment',
      consentPrivacy: 'I have read and accept the privacy policy',
      consentSMS: 'I agree to receive SMS notifications',
      consentMarketing: 'I agree to receive marketing materials',
      signature: 'Signature',
      clearSignature: 'Clear Signature',
      howDidYouHear: 'How did you hear about us?',
      submitting: 'Submitting...',
      successTitle: 'Form Submitted Successfully!',
      successMessage: 'Thank you for filling out the form. A clinic representative will contact you soon.',
      errorRequired: 'Please fill in all required fields',
      addCustom: 'Add',
      yes: 'Yes',
      no: 'No',
      select: 'Select...',
      none: 'None',
      lessThan6Months: 'Less than 6 months',
      sixTo12Months: '6-12 months',
      oneToTwoYears: '1-2 years',
      moreThan2Years: 'More than 2 years',
      never: 'Never'
    }
  }[language];

  // Fetch config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/intake/config?language=${language}`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
        }
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };
    fetchConfig();
  }, [language]);

  // Dental visit options
  const dentalVisitOptions = [
    { value: '', label: t.select },
    { value: 'less_than_6_months', label: t.lessThan6Months },
    { value: '6_to_12_months', label: t.sixTo12Months },
    { value: '1_to_2_years', label: t.oneToTwoYears },
    { value: 'more_than_2_years', label: t.moreThan2Years },
    { value: 'never', label: t.never }
  ];

  // Toggle array item
  const toggleArrayItem = (path, item) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      const lastKey = keys[keys.length - 1];
      const arr = current[lastKey] || [];

      if (arr.includes(item)) {
        current[lastKey] = arr.filter(x => x !== item);
      } else {
        current[lastKey] = [...arr, item];
      }

      return newData;
    });
  };

  // Add custom item to array
  const addCustomItem = (path, value, setCustomValue) => {
    if (!value.trim()) return;
    toggleArrayItem(path, value.trim());
    setCustomValue('');
  };

  // Update nested field
  const updateField = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Validate step
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.first_name && formData.last_name && formData.phone && formData.date_of_birth;
      case 2:
        return formData.emergency_contact.name && formData.emergency_contact.phone;
      case 3:
        return true; // Medical history is optional
      case 4:
        return true; // Insurance is optional
      case 5:
        return formData.consent_treatment && formData.consent_privacy;
      default:
        return true;
    }
  };

  // Navigate steps
  const nextStep = () => {
    if (!validateStep(currentStep)) {
      setError(t.errorRequired);
      return;
    }
    setError(null);
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const prevStep = () => {
    setError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateStep(5)) {
      setError(t.errorRequired);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/intake/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Submission failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Signature pad (simplified)
  const clearSignature = () => {
    const canvas = signatureRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      updateField('signature', '');
    }
  };

  const initSignaturePad = useCallback(() => {
    const canvas = signatureRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let isDrawing = false;

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    };

    const startDrawing = (e) => {
      isDrawing = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      isDrawing = false;
      updateField('signature', canvas.toDataURL());
    };

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', stopDrawing);
  }, []);

  useEffect(() => {
    if (currentStep === 5) {
      setTimeout(initSignaturePad, 100);
    }
  }, [currentStep, initSignaturePad]);

  // Success screen
  if (submitted) {
    return (
      <div className={`intake-form ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="success-screen">
          <div className="success-icon">✓</div>
          <h2>{t.successTitle}</h2>
          <p>{t.successMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`intake-form ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className="intake-header">
        <div className="header-content">
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
        <button
          className="lang-toggle"
          onClick={() => setLanguage(language === 'he' ? 'en' : 'he')}
        >
          {language === 'he' ? 'EN' : 'עב'}
        </button>
      </div>

      {/* Progress */}
      <div className="progress-bar">
        {[1, 2, 3, 4, 5].map(step => (
          <div
            key={step}
            className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
          >
            <span className="step-number">{step}</span>
            <span className="step-label">{t[`step${step}`]}</span>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && <div className="intake-error">{error}</div>}

      {/* Form Content */}
      <div className="form-content">
        {/* Step 1: Personal Info */}
        {currentStep === 1 && (
          <div className="form-step">
            <h2>{t.step1}</h2>

            <div className="form-row">
              <div className="form-group">
                <label>{t.firstName} *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t.lastName} *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t.dateOfBirth} *</label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => updateField('date_of_birth', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t.gender}</label>
                <select
                  value={formData.gender}
                  onChange={(e) => updateField('gender', e.target.value)}
                >
                  <option value="">{t.select}</option>
                  {config?.genders?.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t.phone} *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t.idNumber}</label>
                <input
                  type="text"
                  value={formData.id_number}
                  onChange={(e) => updateField('id_number', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t.email}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t.address}</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t.city}</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t.preferredLang}</label>
              <select
                value={formData.preferred_language}
                onChange={(e) => updateField('preferred_language', e.target.value)}
              >
                <option value="he">{t.hebrew}</option>
                <option value="en">{t.english}</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Emergency Contact */}
        {currentStep === 2 && (
          <div className="form-step">
            <h2>{t.step2}</h2>

            <div className="form-group">
              <label>{t.contactName} *</label>
              <input
                type="text"
                value={formData.emergency_contact.name}
                onChange={(e) => updateField('emergency_contact.name', e.target.value)}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t.relationship}</label>
                <input
                  type="text"
                  value={formData.emergency_contact.relationship}
                  onChange={(e) => updateField('emergency_contact.relationship', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t.contactPhone} *</label>
                <input
                  type="tel"
                  value={formData.emergency_contact.phone}
                  onChange={(e) => updateField('emergency_contact.phone', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Medical History */}
        {currentStep === 3 && (
          <div className="form-step">
            <h2>{t.step3}</h2>

            {/* Allergies */}
            <div className="form-group">
              <label>{t.allergies}</label>
              <div className="chip-input">
                {formData.medical_history.allergies.map(item => (
                  <span key={item} className="chip">
                    {item}
                    <button onClick={() => toggleArrayItem('medical_history.allergies', item)} title={language === 'he' ? 'הסר' : 'Remove'}>×</button>
                  </span>
                ))}
                <div className="add-custom">
                  <input
                    type="text"
                    value={customAllergy}
                    onChange={(e) => setCustomAllergy(e.target.value)}
                    placeholder={t.addCustom}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomItem('medical_history.allergies', customAllergy, setCustomAllergy)}
                  />
                  <button onClick={() => addCustomItem('medical_history.allergies', customAllergy, setCustomAllergy)}>+</button>
                </div>
              </div>
            </div>

            {/* Medications */}
            <div className="form-group">
              <label>{t.medications}</label>
              <div className="chip-input">
                {formData.medical_history.medications.map(item => (
                  <span key={item} className="chip">
                    {item}
                    <button onClick={() => toggleArrayItem('medical_history.medications', item)} title={language === 'he' ? 'הסר' : 'Remove'}>×</button>
                  </span>
                ))}
                <div className="add-custom">
                  <input
                    type="text"
                    value={customMedication}
                    onChange={(e) => setCustomMedication(e.target.value)}
                    placeholder={t.addCustom}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomItem('medical_history.medications', customMedication, setCustomMedication)}
                  />
                  <button onClick={() => addCustomItem('medical_history.medications', customMedication, setCustomMedication)}>+</button>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="form-group">
              <label>{t.conditions}</label>
              <div className="checkbox-grid">
                {config?.chronic_conditions?.map(condition => (
                  <label key={condition} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.medical_history.chronic_conditions.includes(condition)}
                      onChange={() => toggleArrayItem('medical_history.chronic_conditions', condition)}
                    />
                    {condition}
                  </label>
                ))}
              </div>
            </div>

            {/* Blood Type */}
            <div className="form-row">
              <div className="form-group">
                <label>{t.bloodType}</label>
                <select
                  value={formData.medical_history.blood_type}
                  onChange={(e) => updateField('medical_history.blood_type', e.target.value)}
                >
                  <option value="">{t.select}</option>
                  {config?.blood_types?.map(bt => (
                    <option key={bt} value={bt}>{bt}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t.smoker}</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="smoker"
                      checked={formData.medical_history.smoker}
                      onChange={() => updateField('medical_history.smoker', true)}
                    />
                    {t.yes}
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="smoker"
                      checked={!formData.medical_history.smoker}
                      onChange={() => updateField('medical_history.smoker', false)}
                    />
                    {t.no}
                  </label>
                </div>
              </div>
            </div>

            {/* Dental Section */}
            <h3 className="section-title">🦷</h3>

            <div className="form-group">
              <label>{t.lastDentalVisit}</label>
              <select
                value={formData.medical_history.last_dental_visit}
                onChange={(e) => updateField('medical_history.last_dental_visit', e.target.value)}
              >
                {dentalVisitOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t.dentalConcerns}</label>
              <div className="checkbox-grid">
                {config?.dental_concerns?.map(concern => (
                  <label key={concern} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.medical_history.dental_concerns.includes(concern)}
                      onChange={() => toggleArrayItem('medical_history.dental_concerns', concern)}
                    />
                    {concern}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-row three-col">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.medical_history.gum_disease_history}
                  onChange={(e) => updateField('medical_history.gum_disease_history', e.target.checked)}
                />
                {t.gumDisease}
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.medical_history.teeth_grinding}
                  onChange={(e) => updateField('medical_history.teeth_grinding', e.target.checked)}
                />
                {t.teethGrinding}
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.medical_history.dental_anxiety}
                  onChange={(e) => updateField('medical_history.dental_anxiety', e.target.checked)}
                />
                {t.dentalAnxiety}
              </label>
            </div>
          </div>
        )}

        {/* Step 4: Insurance */}
        {currentStep === 4 && (
          <div className="form-step">
            <h2>{t.step4}</h2>

            <div className="form-group">
              <label>{t.insuranceProvider}</label>
              <input
                type="text"
                value={formData.insurance_info.provider}
                onChange={(e) => updateField('insurance_info.provider', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t.policyNumber}</label>
                <input
                  type="text"
                  value={formData.insurance_info.policy_number}
                  onChange={(e) => updateField('insurance_info.policy_number', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t.subscriberId}</label>
                <input
                  type="text"
                  value={formData.insurance_info.subscriber_id}
                  onChange={(e) => updateField('insurance_info.subscriber_id', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t.subscriberName}</label>
              <input
                type="text"
                value={formData.insurance_info.subscriber_name}
                onChange={(e) => updateField('insurance_info.subscriber_name', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Step 5: Consent */}
        {currentStep === 5 && (
          <div className="form-step">
            <h2>{t.step5}</h2>

            <div className="consent-items">
              <label className="consent-item required">
                <input
                  type="checkbox"
                  checked={formData.consent_treatment}
                  onChange={(e) => updateField('consent_treatment', e.target.checked)}
                />
                <span>{t.consentTreatment} *</span>
              </label>

              <label className="consent-item required">
                <input
                  type="checkbox"
                  checked={formData.consent_privacy}
                  onChange={(e) => updateField('consent_privacy', e.target.checked)}
                />
                <span>{t.consentPrivacy} *</span>
              </label>

              <label className="consent-item">
                <input
                  type="checkbox"
                  checked={formData.consent_sms}
                  onChange={(e) => updateField('consent_sms', e.target.checked)}
                />
                <span>{t.consentSMS}</span>
              </label>

              <label className="consent-item">
                <input
                  type="checkbox"
                  checked={formData.consent_marketing}
                  onChange={(e) => updateField('consent_marketing', e.target.checked)}
                />
                <span>{t.consentMarketing}</span>
              </label>
            </div>

            <div className="form-group">
              <label>{t.howDidYouHear}</label>
              <select
                value={formData.how_did_you_hear}
                onChange={(e) => updateField('how_did_you_hear', e.target.value)}
              >
                <option value="">{t.select}</option>
                {config?.referral_sources?.map(src => (
                  <option key={src} value={src}>{src}</option>
                ))}
              </select>
            </div>

            <div className="form-group signature-group">
              <label>{t.signature}</label>
              <canvas
                ref={signatureRef}
                width={400}
                height={150}
                className="signature-pad"
              />
              <button type="button" className="clear-signature" onClick={clearSignature}>
                {t.clearSignature}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="form-navigation">
        {currentStep > 1 && (
          <button className="btn-secondary" onClick={prevStep}>
            {t.back}
          </button>
        )}
        {currentStep < 5 ? (
          <button className="btn-primary" onClick={nextStep}>
            {t.next}
          </button>
        ) : (
          <button
            className={`btn-primary submit ${isViewOnly ? 'disabled-view-only' : ''}`}
            onClick={handleSubmit}
            disabled={loading || isViewOnly}
            title={isViewOnly ? viewOnlyTip : undefined}
          >
            {loading ? t.submitting : t.submit}
          </button>
        )}
      </div>
    </div>
  );
};

export default IntakeForm;
